/**
 * Compact lead-detail cards shared by lead detail and source review.
 */
import { formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize, pill } from "./dashboard-html.js";
import { taskAssignmentFormHtml } from "./dashboard-task-form.js";
import type {
  LeadFactoryContactMethod,
  LeadFactoryEvent,
  LeadFactoryFact,
  LeadFactoryOutreachAttempt,
  LeadFactoryOwner,
  LeadFactoryPipelineEntry,
  LeadFactorySegmentAssignment,
  LeadFactoryTask
} from "./dashboard-lead-factory-types.js";

export function segmentDetailHtml(segment: LeadFactorySegmentAssignment): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(segment.label || segment.segment_key)}</strong>
        <small>${escapeHtml(segment.family || "segment")} · ${escapeHtml(segment.assigned_by || "unknown")}</small>
        <p>${escapeHtml(segment.description || segment.evidence || "No segment detail.")}</p>
      </div>
      ${pill(`${Math.round((segment.confidence ?? 0) * 100)}%`, segment.segment_key)}
    </article>
  `;
}

export function taskDetailHtml(task: LeadFactoryTask): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(labelize(task.task_type))}</strong>
        <small>${escapeHtml(task.status)} · ${escapeHtml(task.priority)}${task.assigned_to ? ` · ${escapeHtml(task.assigned_to)}` : ""}</small>
        <p>${escapeHtml(task.instructions || task.blocking_reason || "No task instructions.")}</p>
      </div>
      <div class="inline-actions">
        ${taskAssignmentFormHtml(task)}
        ${pill(labelize(task.status), task.status)}
      </div>
    </article>
  `;
}

export function ownerDetailHtml(owner: LeadFactoryOwner): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(owner.name)}</strong>
        <small>owner · ${Math.round((owner.confidence ?? 0) * 100)}% confidence</small>
        <p>${escapeHtml(owner.source_fact_id || "No source fact linked.")}</p>
      </div>
    </article>
  `;
}

export function contactDetailHtml(contact: LeadFactoryContactMethod): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(contact.value)}</strong>
        <small>${escapeHtml(contact.method_type)} · ${escapeHtml(contact.status)} · ${escapeHtml(contact.source)}</small>
        <p>${escapeHtml(contact.owner_id || "Lead-level contact method.")}</p>
      </div>
    </article>
  `;
}

export function factDetailHtml(fact: LeadFactoryFact, evidenceId?: string): string {
  return `
    <article class="task-item compact" ${evidenceId ? `id="${escapeAttr(evidenceId)}"` : ""}>
      <div>
        <strong>${escapeHtml(labelize(fact.field_name))}</strong>
        <small>${escapeHtml(fact.source_document_id)} · ${escapeHtml(fact.provenance)}</small>
        <p>${escapeHtml(fact.normalized_value || fact.value)}</p>
      </div>
      <div class="inline-actions">
        <form class="structured-action-form fact-correction-form" data-fact-correction-form data-fact-id="${escapeAttr(fact.id)}" data-fact-field="${escapeAttr(fact.field_name)}">
          <input type="text" maxlength="240" value="${escapeAttr(fact.normalized_value || fact.value)}" aria-label="Correct ${escapeAttr(labelize(fact.field_name))}" data-fact-correction-value>
          <input type="text" maxlength="80" value="operator_review" aria-label="Correction reason" data-fact-correction-reason>
          <button class="button secondary" type="submit">Correct</button>
        </form>
        <button class="button secondary" type="button" data-fact-reject data-fact-id="${escapeAttr(fact.id)}" data-fact-field="${escapeAttr(fact.field_name)}">Reject</button>
        ${evidenceId ? `<button class="button secondary" type="button" data-evidence-jump="#${escapeAttr(evidenceId)}">Focus</button>` : ""}
        ${pill(`${Math.round((fact.confidence ?? 0) * 100)}%`, fact.review_status || "fact")}
      </div>
    </article>
  `;
}

export function outreachDetailHtml(outreach: LeadFactoryOutreachAttempt): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(labelize(outreach.channel))}</strong>
        <small>${escapeHtml(outreach.outcome)} · ${escapeHtml(outreach.actor)} · ${escapeHtml(outreach.attempted_at)}</small>
        <p>${escapeHtml(outreach.note || outreach.follow_up_at || "No outreach note.")}</p>
      </div>
      ${pill(labelize(outreach.outcome), outreach.outcome)}
    </article>
  `;
}

export function pipelineDetailHtml(entry: LeadFactoryPipelineEntry): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(labelize(entry.stage))}</strong>
        <small>${escapeHtml(entry.assigned_to || "unassigned")} · ${escapeHtml(entry.updated_at)}</small>
        <p>${escapeHtml(entry.value_estimate ? `$${formatInteger(entry.value_estimate)} value estimate` : "No value estimate.")}</p>
      </div>
      ${pill(labelize(entry.stage), entry.stage)}
    </article>
  `;
}

export function eventDetailHtml(event: LeadFactoryEvent): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(labelize(event.event_type))}</strong>
        <small>${escapeHtml(event.actor)} · ${escapeHtml(event.created_at)}</small>
        <p>${escapeHtml(event.note || event.source_document_id || "No event note.")}</p>
      </div>
    </article>
  `;
}

export function pipelineEntryHtml(entry: LeadFactoryPipelineEntry): string {
  const leadLabel = entry.property_address || entry.owner_name || entry.property_lead_id;
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(leadLabel)}</strong>
        <small>${escapeHtml(labelize(entry.stage))} · ${escapeHtml(entry.county || "unknown county")}</small>
        <p>${escapeHtml(entry.value_estimate ? `$${formatInteger(entry.value_estimate)} estimate` : "No value estimate yet.")}</p>
      </div>
      ${pill(labelize(entry.lead_status || "pipeline"), entry.stage)}
    </article>
  `;
}
